import { differenceInWeeks, isThisYear, isToday, isTomorrow, isYesterday, getMonth, format, parse } from 'date-fns'

/**
 * String formatting rules as decided by UX team for dates returned from the delivery zones app.
 * https://youfoodz.atlassian.net/wiki/spaces/YOUF/pages/924549197/Date+Time+Formatting
 */
export class DeliveryZoneDate {
  deliveryDate: Date

  constructor(deliveryDate: string) {
    // When constructing js dates, it's necessary to split each of the values as
    // each browser handles date string interpretation differently.
    // Expected input: YYYY-MM-DD
    const dateSplit: string[] = deliveryDate?.split('-')

    this.deliveryDate = new Date(parseInt(dateSplit?.[0], 10), parseInt(dateSplit?.[1], 10) - 1, parseInt(dateSplit?.[2], 10))
  }

  get isTomorrow(): boolean {
    return isTomorrow(this.deliveryDate)
  }

  get day(): string {
    const days: string[] = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

    return days[this.deliveryDate.getDay()]
  }

  get dayShort(): string {
    const days: string[] = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

    return days[this.deliveryDate.getDay()]
  }

  get isRelative(): boolean {
    return isYesterday(this.deliveryDate) || isToday(this.deliveryDate) || isTomorrow(this.deliveryDate)
  }

  get dayRelative(): string {
    if (isYesterday(this.deliveryDate)) {
      return 'Yesterday'
    }

    if (isToday(this.deliveryDate)) {
      return 'Today'
    }

    if (isTomorrow(this.deliveryDate)) {
      return 'Tomorrow'
    }

    return this.day
  }

  get dayRelativeTimeAware(): string {
    if (isToday(this.deliveryDate) && this.deliveryDate.getHours() >= 18) {
      return 'Tonight'
    }

    return this.dayRelative
  }

  get month(): string {
    return format(this.deliveryDate, 'MMMM')
  }

  get monthShort(): string {
    return format(this.deliveryDate, 'MMM')
  }

  get date(): string {
    return `${this.month} ${this.deliveryDate.getDate()}, ${this.deliveryDate.getFullYear()}`
  }

  get dateDay(): number {
    return this.deliveryDate.getDate()
  }

  get dateShort(): string {
    return `${this.monthShort} ${this.deliveryDate.getDate()}, ${this.deliveryDate.getFullYear()}`
  }

  get dateShortVeryMinimal(): string {
    return format(this.deliveryDate, 'D/M')
  }

  get dateMinimal(): string {
    if (isThisYear(this.deliveryDate)) {
      return `${this.month} ${this.deliveryDate.getDate()}`
    }

    return this.date
  }

  get dateShortMinimal(): string {
    if (isThisYear(this.deliveryDate)) {
      return `${this.monthShort} ${format(this.deliveryDate, 'do')}`
    }

    return this.dateShort
  }

  get dayDate(): string {
    return `${this.day}, ${this.date}`
  }

  get dayDateMinimal(): string {
    if (differenceInWeeks(new Date(), this.deliveryDate) > 1) {
      return `${this.day}, ${this.dateMinimal}`
    }

    return this.day
  }

  get dayDateRelativeMinimal(): string {
    if (differenceInWeeks(new Date(), this.deliveryDate) > 1) {
      return `${this.dayRelativeTimeAware}, ${this.dateMinimal}`
    }

    return this.dayRelativeTimeAware
  }

  get dayDateRelativeMinimalV2(): string {
    if (differenceInWeeks(new Date(), this.deliveryDate) > 1) {
      return `${this.dayRelativeTimeAware}, ${this.dateMinimal}`
    }

    return this.dayRelativeTimeAware
  }

  get dayDateRelativeShortMinimal(): string {
    if (differenceInWeeks(new Date(), this.deliveryDate) > 1) {
      return `${this.dayShort}, ${this.dateShortMinimal}`
    }

    return this.dayRelativeTimeAware
  }

  get time(): string {
    return `${this.deliveryDate.getHours() % 12}
    ${this.deliveryDate.getMinutes() > 0 ? `:${this.deliveryDate.getMinutes()}` : ''}
    ${this.deliveryDate.getHours() > 12 ? 'pm' : 'am'}`.replace(/\s/g, '')
  }

  get timeRelative(): string {
    if (this.deliveryDate.getHours() >= 23 && this.deliveryDate.getMinutes() >= 59) {
      return 'Midnight'
    }

    if (
      (this.deliveryDate.getHours() === 11 && this.deliveryDate.getMinutes() === 59) ||
      (this.deliveryDate.getHours() === 12 && this.deliveryDate.getMinutes() === 0)
    ) {
      return 'Midday'
    }

    return this.time
  }

  get dayDateTimeRelativeMinimal(): string {
    return `${this.dayDateRelativeMinimal}`
  }

  get dayDateTimeRelativeVeryMinimal(): string {
    if (!isToday(this.deliveryDate)) {
      return this.dayDateTimeRelativeMinimal
    }

    return this.timeRelative
  }

  get dayDateTimeRelativeShortMinimal(): string {
    return `${this.dayDateRelativeShortMinimal}`
  }

  get dayDateTimeRelativeShortVeryMinimal(): string {
    if (!isToday(this.deliveryDate)) {
      return this.dayDateTimeRelativeShortMinimal
    }

    return this.timeRelative
  }
}
